import { ChangeEvent, FC, ReactNode, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import { styled } from '@mui/material';
import { toast } from 'react-toastify';
import dayjs, { Dayjs } from 'dayjs';

import { useCheckPermission, useCompareDates } from '../../shared/Helpers/hooks';
import { CustomButton, CustomCheckBox, Picker, Text } from '../../shared/uiComponents';
import { dateNormalizer, formatClientName } from '../../shared/Helpers/functions';
import SearchSelect, { MultipleSearchSelect } from '../../shared/uiComponents/Dropdown/searchSelect';
import { PERMISSIONS } from '../../App/constants';
import { DataProperties } from '../../shared/uiComponents/Radio';
import { TEXT } from '../../shared/Helpers/colors';

import { DispatchProperties, useSelector } from '../../redux/store';
import { getUsers } from '../../redux/State/userSlice';
import { getTherapists, getUserClients } from '../../redux/State/clientSlice/userClientSlice';
import { getClients } from '../../redux/State/clientSlice/clientSlice';
import {
	setClientId,
	setClientIds,
	setEndDate,
	setOnlyNew,
	setSearchStringClient,
	setSearchStringUser,
	setStartDate,
	// clearFilterData,
	setTimeError,
	setUserId,
} from '../../redux/State/clientSlice/billingSlice';

const today = dayjs();
const startDayOfMonth = today.startOf('month');
const beginningOfLastMoth = startDayOfMonth.subtract(1, 'month');
const endOfLastMoth = beginningOfLastMoth.endOf('month');
const startOfYear = today.startOf('year');

export const TimeInputsWrapper = styled('div')(() => ({
	display: 'flex',
	gap: '16px',
	flexWrap: 'wrap',
	marginBottom: '16px',
	'&>div': {
		flex: '1 0 0',
		minWidth: '140px',
	},
	'@media (max-width: 525px)': {
		flexDirection: 'column',
		'&>div': {
			marginBottom: '16px',
		},
	},
}));

export const ReportFilters: FC<{
	afterPeriod?: ReactNode;
	children?: ReactNode;
	clientFunderId?: string;
	combineDateWithShortcuts?: boolean;
	inline?: boolean;
	isMultipleSelect?: boolean;
	rowOneEnd?: ReactNode;
	shortcutsLabel?: string;
	showClients?: boolean;
	showDateRange?: boolean;
	showOnlyNew?: boolean;
	showShorcuts?: boolean;
	showUsers: boolean;
}> = ({
	showUsers,
	showClients = true,
	showShorcuts = true,
	showOnlyNew = true,
	isMultipleSelect = false,
	showDateRange = true,
	inline = false,
	combineDateWithShortcuts = false,
	shortcutsLabel = 'Period',
	children,
	afterPeriod,
	rowOneEnd,
	clientFunderId,
}) => {
	const dispatch = useDispatch<DispatchProperties>();

	const CLIENT = useCheckPermission(PERMISSIONS.CLIENT.READ);
	const USER = useCheckPermission(PERMISSIONS.USER.READ);

	const { clientId, userId, onlyNew, clientIds } = useSelector((state) => state.billing.billingFilters);
	// const loadingUsers = useSelector((state) => state.user.loading);
	// const loadingClients = useSelector((state) => state.client.loading);
	const loadingBilling = useSelector((state) => state.billing.loading);
	// const loadingExport = useSelector((state) => state.export.loading);

	const [startDateValue, setStartDateValue] = useState<Dayjs>(startDayOfMonth);
	const [endDateValue, setEndDateValue] = useState<Dayjs>(today);

	const searchClient = useSelector((state) => state.billing.billingFilters.searchStringClient);
	const searchUser = useSelector((state) => state.billing.billingFilters.searchStringUser);

	const [pageSizeUser, setPageSizeUser] = useState<number>(10);
	const [pageSizeClient, setPageSizeClient] = useState<number>(10);
	const [usersData, setUsersData] = useState<DataProperties[]>([]);
	const [clientsData, setClientsData] = useState<DataProperties[]>([]);
	const [hasMoreClient, setHasMoreClient] = useState<boolean>(false);
	const [hasMoreUser, setHasMoreUser] = useState<boolean>(false);

	const { hasError, message } = useCompareDates(startDateValue, endDateValue);

	useEffect(() => {
		dispatch(setTimeError(hasError));
	}, [dispatch, hasError]);

	// useEffect(() => {
	//   //clearing previously entered data for filters on new page
	//   dispatch(clearFilterData())
	// }, [dispatch])

	useEffect(() => {
		if (!startDateValue || !endDateValue) return;
		const start = dateNormalizer(startDateValue);
		const end = dateNormalizer(endDateValue);
		dispatch(setStartDate(start));
		dispatch(setEndDate(end));
	}, [startDateValue, endDateValue, dispatch]);

	useEffect(() => {
		if (!pageSizeUser || Boolean(clientId) || !USER.permissionGranted || !showUsers) return;
		const timeout = setTimeout(() => {
			dispatch(
				getUsers({
					page: '1',
					pageSize: `${pageSizeUser}`,
					searchString: searchUser,
				}),
			)
				.then(unwrapResult)
				.then((response) => {
					const { query, hasNextPage } = response;
					if (!query) return;
					const data: DataProperties[] = query.map((user) => ({
						id: user.id,
						label: user.fullName,
					}));
					setUsersData(data);
					setHasMoreUser(Boolean(hasNextPage));
				});
		}, 1000);

		// TODO - return void, clearTimeout, test
		// eslint-disable-next-line
		return () => clearTimeout(timeout);
	}, [dispatch, pageSizeUser, searchUser, clientId, USER.permissionGranted, showUsers]);

	useEffect(() => {
		if (!clientId || !USER.permissionGranted || !showUsers) return;

		const timeout = setTimeout(() => {
			dispatch(getTherapists({ clientId }))
				.then(unwrapResult)
				.then((response) => {
					if (!response) return;
					const data: DataProperties[] = response.map((user) => ({
						id: user.id,
						label: user.fullName,
					}));
					setUsersData(data);
				});
		}, 1000);

		// TODO - return void, clearTimeout, test
		// eslint-disable-next-line
		return () => clearTimeout(timeout);
	}, [dispatch, clientId, USER.permissionGranted, showUsers]);

	useEffect(() => {
		if (!pageSizeClient || !CLIENT.permissionGranted || !showClients) return;

		if (!userId) {
			const timeout = setTimeout(() => {
				dispatch(
					getClients({
						page: '1',
						pageSize: `${pageSizeClient}`,
						searchString: searchClient,
						funderId: clientFunderId,
					}),
				)
					.then(unwrapResult)
					.then((response) => {
						const { query, hasNextPage } = response;
						if (!query) return;
						const data: DataProperties[] = query.map((client) => ({
							id: client.id,
							label: formatClientName(client.fullName ?? '', client.isElc),
						}));
						setClientsData(data);
						setHasMoreClient(Boolean(hasNextPage));
					});
			}, 1000);

			// TODO - return void, clearTimeout, test
			// eslint-disable-next-line
			return () => clearTimeout(timeout);
		}

		const timeout = setTimeout(() => {
			dispatch(
				getUserClients({
					userId,
					page: '1',
					pageSize: `${pageSizeClient}`,
					searchString: searchClient,
				}),
			)
				.then(unwrapResult)
				.then((response) => {
					const { query, hasNextPage } = response;
					if (!query) return;
					const data: DataProperties[] = query.map((client) => ({
						id: client.id,
						label: formatClientName(client.fullName ?? '', client.isElc),
					}));
					setClientsData(data);
					setHasMoreClient(Boolean(hasNextPage));
				});
		}, 1000);

		// TODO - return void, clearTimeout, test
		// eslint-disable-next-line
		return () => clearTimeout(timeout);
	}, [dispatch, pageSizeClient, searchClient, userId, CLIENT.permissionGranted, showClients]);

	const startDateHandler = (date: Dayjs | null) => {
		if (!date) return;
		setStartDateValue(date);
	};

	const endDateHandler = (date: Dayjs | null) => {
		if (!date) return;
		setEndDateValue(date);
	};

	const userHandler = (value: string) => {
		if (!value) return;
		dispatch(setUserId(value));
	};

	const clientHandler = (value: string) => {
		if (!value) return;
		dispatch(setClientId(value));
	};

	const userSearchStringHandler = (value: string) => {
		dispatch(setSearchStringUser(value));
	};

	const clientSearchStringHandler = (value: string) => {
		dispatch(setSearchStringClient(value));
	};

	const onlyNewHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { checked } = event.target;
		dispatch(setOnlyNew(checked));
	};

	const thisMonthHandler = () => {
		setStartDateValue(startDayOfMonth);
		setEndDateValue(today);
	};

	const lastMonthHandler = () => {
		setStartDateValue(beginningOfLastMoth);
		setEndDateValue(endOfLastMoth);
	};

	const thisYearHandler = () => {
		setStartDateValue(startOfYear);
		setEndDateValue(today);
	};

	const onClientIdsChange = (ids: string[]) => {
		if (!ids) return;
		dispatch(setClientIds(ids));
	};

	const shortcutsContent = (
		<>
			{showOnlyNew && (
				<CustomCheckBox
					item={{
						label: 'Show only new',
						id: '0',
						checked: onlyNew,
						disabled: loadingBilling,
					}}
					onChange={onlyNewHandler}
				/>
			)}
			<CustomButton
				title="This month"
				onClick={thisMonthHandler}
				buttonType="secondaryGray"
				disabled={loadingBilling}
			/>
			<CustomButton
				title="Last month"
				onClick={lastMonthHandler}
				buttonType="secondaryGray"
				disabled={loadingBilling}
			/>
			<CustomButton
				title="This year"
				onClick={thisYearHandler}
				buttonType="secondaryGray"
				disabled={loadingBilling}
			/>
		</>
	);

	const shortcutsBlockWithLabel = (
		<div>
			<Text className="inputLabel active" title={shortcutsLabel} type="psm" fontWeight="medium" color={TEXT.PRIMARY} />
			<div style={{ display: 'flex', gap: '8px' }}>{shortcutsContent}</div>
		</div>
	);

	const dateRangeFields = (
		<>
			<Picker.CustomDate
				label="Start Date"
				value={startDateValue}
				onChange={startDateHandler}
				disabled={loadingBilling}
			/>
			<Picker.CustomDate
				label="End Date"
				error={hasError}
				errorMessage={message}
				value={endDateValue}
				onChange={endDateHandler}
				disabled={loadingBilling}
			/>
		</>
	);

	return (
		<>
			<TimeInputsWrapper>
				{CLIENT.permissionGranted && showClients && (
					<div style={{ width: '300px' }}>
						{isMultipleSelect ? (
							<MultipleSearchSelect
								label="Find Clients:"
								values={clientIds}
								data={clientsData}
								setValues={onClientIdsChange}
								searchString={searchClient}
								setSearchString={clientSearchStringHandler}
								hasMore={hasMoreClient}
								setPageSize={setPageSizeClient}
							/>
						) : (
							<SearchSelect
								label="Find Client:"
								searchString={searchClient}
								setSearchString={clientSearchStringHandler}
								data={clientsData}
								setValue={clientHandler}
								hasMore={hasMoreClient}
								setSize={setPageSizeClient}
								clearData={() => dispatch(setClientId(''))}
							/>
						)}
					</div>
				)}
				{USER.permissionGranted && showUsers && (
					<div style={{ width: '250px' }}>
						<SearchSelect
							label="Find Provider:"
							searchString={searchUser}
							setSearchString={userSearchStringHandler}
							data={usersData.filter((x) =>
								clientId ? x.label?.toLowerCase().includes(searchUser.toLowerCase()) : true,
							)}
							setValue={userHandler}
							hasMore={hasMoreUser}
							setSize={setPageSizeUser}
							clearData={() => dispatch(setUserId(''))}
						/>
					</div>
				)}
				{showDateRange && !combineDateWithShortcuts && dateRangeFields}
				{children}
				{inline && showShorcuts && shortcutsBlockWithLabel}
				{!inline && rowOneEnd}
				{inline && afterPeriod}
			</TimeInputsWrapper>

			{!inline && showShorcuts && (
				<TimeInputsWrapper>
					{combineDateWithShortcuts ? shortcutsBlockWithLabel : shortcutsContent}
					{combineDateWithShortcuts && showDateRange && dateRangeFields}
					{combineDateWithShortcuts && afterPeriod}
				</TimeInputsWrapper>
			)}
		</>
	);
};

export const downloadFile = ({ file, fileName }: { file: Blob; fileName: string }) => {
	const objectURL = URL.createObjectURL(file);
	const link = document.createElement('a');
	link.href = objectURL;
	link.setAttribute('download', fileName);
	document.body.appendChild(link);
	link.click();
	toast('Downloaded');
};
